import { chameleonApiKey } from '@/environment';
import { useGetAgents } from '@/pages/agents/api';
import { getCurrentSubscription } from '@/services/api';
import { DidUser, useAuth } from '@/services/auth';
import { useFeatureFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { useChatSessionsQuery } from '@/state/chat-sessions/useChatSessions';
import { useCreditQuery } from '@/state/credits/credits';
import { useGetTranslationGroups } from '@/state/translations';
import { PaymentProvider } from '@/state/user-settings/types';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { useGetProjects } from '@/state/video-library/videoLibrary';
import { isDidUser } from '@/utils/utils';
import { useAuth0 } from '@auth0/auth0-react';
import chmln from '@chamaeleonidae/chmln';
import { AgentStatus, PlanGroup, ProductType } from '@d-id/types';
import { ReactNode, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { GET_CURRENT_SUBSCRIPTION_KEY } from '../constants';

chmln.init(chameleonApiKey, { fastUrl: 'https://fast.chameleon.io/' });

export enum ChameleonEvent {
    QuestionnaireDone = 'questionnaire-done',
}

interface Props {
    children?: ReactNode;
}

interface IdentityData extends Record<string, any> {
    appsVisited?: string[];
    billingInterval?: 'month' | 'year';
    credits?: number;
    credits_left?: number;
    free_sessions_left?: number;
    agents_count?: number;
    given_name?: string;
    user_created_at?: Date;
    isTrial?: boolean;
    onboard_class?: string;
    onboard_company_name?: string;
    onboard_company_size?: string;
    onboard_description?: string;
    onboard_done?: string;
    onboard_usecases?: string;
    org_id?: string;
    planGroup?: PlanGroup;
    productName?: ProductType;
    provider?: PaymentProvider;
    stripeProductName?: string;
}

const mapFeatureFlags = (flags: Record<string, any>) => {
    return Object.entries(flags).reduce((acc, cur) => ({ ...acc, [`ff_` + cur[0]]: cur[1] }), {});
};

const useCollectChameleonIdentityData = () => {
    const { user } = useAuth();
    const { userSettings } = useUserSettings();
    const { data: credits, isFetched: isCreditsFetched } = useCreditQuery();
    const { data: chatSessions, isFetched: isChatSessionsFetched } = useChatSessionsQuery();
    const { data: agentLibrary, isLoading: isAgentLibraryLoading } = useGetAgents(user?.isAnonymous);
    const { data: translationsLibrary, isLoading: isTranslationLoading } = useGetTranslationGroups(user?.isAnonymous);
    const { data: projects } = useGetProjects({ isAnonymous: user?.isAnonymous });
    const { user: authUser } = useAuth0();
    const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION_KEY, getCurrentSubscription, {
        enabled: !!user?.stripeSubscriptionId,
    });

    const featureFlagProvider = useFeatureFlag();

    const getData = useCallback((): IdentityData | undefined => {
        if (!user || !isCreditsFetched || !isChatSessionsFetched || isAgentLibraryLoading || isTranslationLoading) {
            return;
        }

        const agents = (agentLibrary?.items || []).filter(item => item.status !== AgentStatus.Rejected);
        const translations = translationsLibrary?.groups || [];
        const onBoardAnswers = userSettings?.features?.onBoarding?.answers?.state;
        const provider = authUser?.['https://d-id.com/provider'];
        const stripeProductName = authUser?.['https://d-id.com/stripe_product_name'];
        const mappedFlags = featureFlagProvider ? mapFeatureFlags(featureFlagProvider.allFlags()) : {};
        const videosCount1stPage = projects?.pages[0].items.length;
        const isInternalUser = user.email ? isDidUser(user.email) : false;
        const paymentSuccessful =
            subscriptionData?.subscription?.status === 'past_due' &&
            typeof subscriptionData.subscription.latest_invoice === 'object' &&
            subscriptionData.subscription.latest_invoice?.status !== 'paid';
        const activeCreditRows = (credits?.credits || []).filter(
            creditRow => new Date(creditRow?.valid_from) < new Date() && new Date(creditRow?.expire_at) >= new Date()
        );
        const activePlanGroup: string | null = activeCreditRows[0]?.plan_group;

        if (credits?.remaining && credits?.total && credits?.remaining > credits.total) {
            logger.error('Credits remaining is greater than total credits', { user, credits });
        }

        return {
            appsVisited: user.appsVisited,
            billingInterval: user.billingInterval,
            credits: credits?.remaining,
            credits_left: credits?.remaining,
            percentage_credits_used: credits?.remaining && credits?.total ? (1 - credits.remaining / credits.total) * 100 : 0,
            free_sessions_left: chatSessions?.freeLeft,
            agents_count: agents.length,
            translations_count: translations.length,
            payment_successful: paymentSuccessful,
            given_name: user.given_name,
            user_created_at: user.createdAt,
            isPrimary: !!user.isPrimary,
            active_subscription_group: activePlanGroup,
            isTrial: user.isTrial,
            videos_count_1st_page: videosCount1stPage,

            onboard_class: onBoardAnswers?.onboard_class,
            onboard_company_name: onBoardAnswers?.onboard_company_name,
            onboard_company_size: onBoardAnswers?.onboard_company_size,
            onboard_description: onBoardAnswers?.onboard_description,
            onboard_done: onBoardAnswers?.onboard_done,
            onboard_usecases: onBoardAnswers?.onboard_usecases,

            onboard_new_audience: onBoardAnswers?.onboard_new_audience,
            onboard_new_class: onBoardAnswers?.onboard_new_class,
            onboard_new_company_role: onBoardAnswers?.onboard_new_company_role,
            onboard_new_company_size: onBoardAnswers?.onboard_new_company_size,
            onboard_new_tool: onBoardAnswers?.onboard_new_tool,
            onboard_new_usecases: onBoardAnswers?.onboard_new_usecases,
            onboard_new_marketing_usecases: onBoardAnswers?.onboard_new_marketing_usecases,
            onboard_new_seniority: onBoardAnswers?.onboard_new_seniority,

            org_id: user.org_id,
            planGroup: user.planGroup,
            productName: user.productName,
            provider,
            stripeProductName,
            workspace_enabled: !!userSettings?.features?.workspaceEnabled,
            ...mappedFlags,
            ...(isInternalUser && { d_id_user: true }),
        };
    }, [
        !!user && isCreditsFetched && isChatSessionsFetched && !isAgentLibraryLoading,
        credits,
        chatSessions,
        agentLibrary,
        user,
        authUser,
        userSettings?.features?.onBoarding?.answers?.state,
        userSettings?.features?.workspaceEnabled,
        featureFlagProvider,
    ]);

    return { getData };
};

const canUseChameleon = (user?: DidUser | null) => {
    const userVerified = user && user.email_verified;
    const userTrialOrPaying = user && user?.planGroup !== PlanGroup.GUEST;

    return user?.sub && userVerified && userTrialOrPaying;
};

export const ChameleonProvider = ({ children }: Props) => {
    const { user } = useAuth();
    const { getData } = useCollectChameleonIdentityData();

    useEffect(() => {
        if (user?.sub && canUseChameleon(user)) {
            const data = getData();
            if (data) {
                chmln.identify(user.sub, data);
            }
        } else {
            window.chmln?.clear();
        }
    }, [user, getData]);

    return <>{children}</>;
};

export const useChameleon = () => {
    const { user } = useAuth();
    const { getData } = useCollectChameleonIdentityData();

    const track = (event: ChameleonEvent) => {
        if (user?.sub && canUseChameleon(user)) {
            chmln.identify(user.sub, getData());
            chmln.track(event);
        }
    };

    return { track };
};
